import React, { useCallback, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { usePrevious } from '@pandiafi/design-system'
import { countryData, CountryDetails } from '@kenno/resources'

import { RecipientNameField } from '../RecipientNameField/RecipientNameField'
import { StreetAddressField } from '../StreetAdressField/StreetAddressField'
import { StreetAddressLine2Field } from '../StreetAddressLine2Field/StreetAddressLine2Field'
import { PostalCodeField } from '../PostalCodeField/PostalCodeField'
import { CityField } from '../CityField/CityField'
import { CountryField } from '../CountryField/CountryField'
import { FormValues } from '../../TerminateForm/initialValues'
import { CountrySubdivisionField } from '../CountrySubdivisionField/CountrySubdivision'
import { isEmpty } from '../../../../../common/validations'

export const PostalAddressFields: React.FC = () => {
  const {
    setFieldValue,
    values: { newAddress: postalAddress },
  } = useFormikContext<FormValues>()

  const onCountryChange = useCallback(
    async (
      countryCode: string | null,
      countryDetails: CountryDetails | null
    ) => {
      await setFieldValue('newAddress.countryCode', countryCode ?? '')
      await setFieldValue(
        'newAddress.countryName',
        countryDetails?.countryName.en.toUpperCase() ?? ''
      )
      if (countryCode === 'FI' || countryCode === null) {
        await setFieldValue('newAddress.countrySubdivision', null)
      }
    },
    [setFieldValue]
  )

  const {
    countryCode,
    countryName: _,
    ...nonCountryValues
  } = postalAddress ?? {}
  const allNonCountryValuesEmpty = allEmpty(Object.values(nonCountryValues))
  const previousAllNonCountryValuesEmpty = usePrevious(allNonCountryValuesEmpty)

  useEffect(() => {
    if (!postalAddress) {
      return
    }
    if (
      previousAllNonCountryValuesEmpty === true &&
      allNonCountryValuesEmpty === false &&
      !countryCode
    ) {
      // User has started filling in the form
      onCountryChange('FI', countryData.FI)
    } else if (
      previousAllNonCountryValuesEmpty === false &&
      allNonCountryValuesEmpty === true &&
      countryCode
    ) {
      // User has cleared all non-country fields in a clearable form
      onCountryChange(null, null)
    }
  }, [
    allNonCountryValuesEmpty,
    countryCode,
    onCountryChange,
    postalAddress,
    previousAllNonCountryValuesEmpty,
  ])

  const isCountrySubdivisionFieldVisible = Boolean(
    postalAddress?.countryCode && postalAddress.countryCode !== 'FI'
  )

  // Full address is required if any field is filled
  const isFullAddressRequired =
    !!postalAddress && !allEmpty(Object.values(postalAddress))

  return (
    <>
      <RecipientNameField required={isFullAddressRequired} />

      <StreetAddressField required={isFullAddressRequired} />

      <StreetAddressLine2Field />

      <PostalCodeField required={isFullAddressRequired} />

      <CityField required={isFullAddressRequired} />

      <CountryField
        onChange={onCountryChange}
        required={isFullAddressRequired}
      />

      {isCountrySubdivisionFieldVisible && <CountrySubdivisionField />}
    </>
  )
}

const allEmpty = (fields: Array<string | null>) => fields.every(isEmpty)
